import { useI18n } from "@core/i18n/i18n";
import { Disease, diseases, FunctionalStatus } from "@domain/medical-condition/medical-condition";
import { WordingKey } from "@typings/i18n-typings";
import { useCallback } from "react";

const diseaseWordingKey = {
	[Disease.IDK]: "disease.idk",
	[Disease.PREFER_NOT_TO_SAY]: "disease.v1_9.preferNotToSay",
	[Disease.OTHER]: "disease.v1_9.other",
	[Disease.SMA]: "disease.v1_9.sma",
	[Disease.ALS]: "disease.v1_9.als",
	[Disease.BMD]: "disease.v1_9.bmd",
	[Disease.FSHD]: "disease.fshd",
	[Disease.LGMD]: "disease.v1_9.lgmd",
	[Disease.CMT]: "disease.v1_9.cmt",
	[Disease.OTHER_MUSCULAR_DYSTROPHY]: "disease.v1_9.otherMuscularDystrophy",
	[Disease.MYASTHENIA]: "disease.v1_9.myasthenia",
	[Disease.NEUROPATHY]: "disease.v1_9.neuropathy",
	[Disease.MYOPATHY]: "disease.v1_9.myopathy",
	[Disease.METABOLIC_DISORDERS]: "disease.v1_9.metabolicDisorders",
	[Disease.FRIEDREICH_ATAXIA]: "disease.friedreichAtaxia",
	[Disease.MULTIPLE_SCLEROSIS]: "disease.multipleSclerosis",
} satisfies Record<Disease, WordingKey>;

const functionalStatusWordingKey = {
	[FunctionalStatus.Walker]: "functionalStatus.walker",
	[FunctionalStatus.Sitter]: "functionalStatus.sitter",
} satisfies Record<FunctionalStatus, WordingKey>;

interface UseFormatMedicalConditionHook {
	formatDisease: (disease?: Disease) => string;
	formatFunctionalStatus: (functionalStatus?: FunctionalStatus) => string;
}

export const useFormatMedicalCondition = (): UseFormatMedicalConditionHook => {
	const { format } = useI18n();

	const formatDisease = useCallback(
		(disease?: Disease) => {
			if (disease && disease !== Disease.IDK) {
				return format(diseaseWordingKey[disease]);
			} else {
				return format("global.empty");
			}
		},
		[format],
	);

	const formatFunctionalStatus = useCallback(
		(functionalStatus?: FunctionalStatus) => {
			if (functionalStatus) {
				return format(functionalStatusWordingKey[functionalStatus]);
			} else {
				return format("global.empty");
			}
		},
		[format],
	);

	return { formatDisease, formatFunctionalStatus };
};

export const useSortedDiseaseList = (): { value: Disease; label: string }[] => {
	const { formatDisease } = useFormatMedicalCondition();
	const collator = new Intl.Collator();
	const [idkChoice, preferNotToSayChoice, otherChoice, ...choicesToSort] = diseases.map(value => ({
		value,
		label: formatDisease(value),
	}));

	return [
		...choicesToSort.sort((d1, d2) => collator.compare(d1.label, d2.label)),
		preferNotToSayChoice,
		otherChoice,
		idkChoice,
	];
};
